import * as Sentry from "@sentry/react";

// 500번대 에러 타입 정의
const SERVER_ERROR_TYPES = {
  500: { name: "internal_server_error", description: "Internal Server Error" },
  501: { name: "not_implemented", description: "Not Implemented" },
  502: { name: "bad_gateway", description: "Bad Gateway" },
  503: { name: "service_unavailable", description: "Service Unavailable" },
  504: { name: "gateway_timeout", description: "Gateway Timeout" },
  505: {
    name: "http_version_not_supported",
    description: "HTTP Version Not Supported",
  },
  506: {
    name: "variant_also_negotiates",
    description: "Variant Also Negotiates",
  },
  507: { name: "insufficient_storage", description: "Insufficient Storage" },
  508: { name: "loop_detected", description: "Loop Detected" },
  509: {
    name: "bandwidth_limit_exceeded",
    description: "Bandwidth Limit Exceeded",
  },
  510: { name: "not_extended", description: "Not Extended" },
  511: {
    name: "network_authentication_required",
    description: "Network Authentication Required",
  },
};

let REACT_APP_VERSION_TYPE = process.env.REACT_APP_VERSION_TYPE;

// Sentry 에러 캡처 함수
const captureSentryError = (
  error,
  endpoint,
  method,
  additionalContext = {},
) => {
  const statusCode = error.status || error.statusCode;

  if (statusCode >= 500 && statusCode <= 599) {
    const errorType = SERVER_ERROR_TYPES[statusCode] || {
      name: `server_error_${statusCode}`,
      description: `Server Error ${statusCode}`,
    };

    Sentry.captureException(error, {
      tags: {
        error_category: "5xx_error",
        error_type: errorType.name,
        http_status: statusCode.toString(),
        http_status_group: "5xx",
        api_endpoint: endpoint,
        api_method: method,
        api_version: REACT_APP_VERSION_TYPE,
      },
      contexts: {
        server_error_details: {
          status_code: statusCode,
          error_name: errorType.name,
          error_description: errorType.description,
          is_timeout: statusCode === 504,
          is_unavailable: statusCode === 503,
        },
        api_request: {
          url: `${process.env.REACT_APP_API_URL}${endpoint}`,
          method: method,
          timestamp: new Date().toISOString(),
          ...additionalContext,
        },
      },
      level: statusCode === 503 || statusCode === 504 ? "warning" : "error",
    });
  }
};

const params = new URLSearchParams(window.location.search);
export const appVersion = params.get("appVersion");

const parseObjToBodyString = (obj) => {
  return Object.keys(obj)
    .map((key) => `${key}=${obj[key]}`)
    .join("&");
};

export default {
  get: (endpoint, riderToken, query = {}) => {
    let isJson = true;

    return fetch(
      `${process.env.REACT_APP_API_HOST_NEW}/${endpoint}?${parseObjToBodyString(
        query,
      )}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${riderToken}`,
          "x-version": process.env.REACT_APP_VERSION_TYPE,
          "X-App-Version": appVersion,
        },
      },
    )
      .then((res) => {
        const contentType = res.headers.get("content-type");

        if (contentType.includes("openxmlformats")) {
          isJson = false;
          return res.blob();
        } else {
          return res.json();
        }
      })
      .then((res) => {
        if (!isJson) {
          return res;
        }

        if (res.statusCode >= 300) {
          const error = new Error();
          error.status = res.statusCode;
          error.statusCode = res.statusCode;
          error.message = res.error || res.message;

          // Sentry 에러 캡처
          captureSentryError(error, endpoint, "GET", { query });

          throw error;
        }

        return res;
      })
      .catch((error) => {
        // 네트워크 에러 등도 캡처
        if (!error.status && !error.statusCode) {
          Sentry.captureException(error, {
            tags: {
              error_category: "network_error",
              error_type: "connection_failed",
              api_endpoint: endpoint,
              api_method: "GET",
            },
          });
        }
        throw error;
      });
  },

  put: (endpoint, riderToken, body = {}) => {
    return fetch(`${process.env.REACT_APP_API_HOST_NEW}/${endpoint}`, {
      body: JSON.stringify(body),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${riderToken}`,
        "x-version": process.env.REACT_APP_VERSION_TYPE,
        "X-App-Version": appVersion,
      },
      method: "PUT",
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.statusCode >= 300) {
          const error = new Error();
          error.status = res.statusCode;
          error.statusCode = res.statusCode;
          error.message = res.error || res.message;

          // Sentry 에러 캡처
          captureSentryError(error, endpoint, "PUT", {
            body_size: JSON.stringify(body).length,
            has_body: Object.keys(body).length > 0,
          });

          throw error;
        }

        return res;
      })
      .catch((error) => {
        // 네트워크 에러 등도 캡처
        if (!error.status && !error.statusCode) {
          Sentry.captureException(error, {
            tags: {
              error_category: "network_error",
              error_type: "connection_failed",
              api_endpoint: endpoint,
              api_method: "PUT",
            },
          });
        }
        throw error;
      });
  },

  post: (endpoint, riderToken, body = {}) => {
    return fetch(`${process.env.REACT_APP_API_HOST_NEW}/${endpoint}`, {
      body: JSON.stringify(body),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${riderToken}`,
        "x-version": process.env.REACT_APP_VERSION_TYPE,
        "X-App-Version": appVersion,
      },
      method: "POST",
    })
      .then((res) => {
        const contentType = res.headers.get("content-type");

        if (contentType.includes("text")) {
          return res.blob();
        } else {
          return res.json();
        }
      })
      .then((res) => {
        if (res.statusCode >= 300) {
          const error = new Error();
          error.status = res.statusCode;
          error.statusCode = res.statusCode;
          error.message = res.error || res.message;

          // Sentry 에러 캡처
          captureSentryError(error, endpoint, "POST", {
            body_size: JSON.stringify(body).length,
            has_body: Object.keys(body).length > 0,
          });

          throw error;
        }

        return res;
      })
      .catch((error) => {
        // 네트워크 에러 등도 캡처
        if (!error.status && !error.statusCode) {
          Sentry.captureException(error, {
            tags: {
              error_category: "network_error",
              error_type: "connection_failed",
              api_endpoint: endpoint,
              api_method: "POST",
            },
          });
        }

        throw error;
      });
  },

  delete: (endpoint, riderToken, body = {}) => {
    return fetch(`${process.env.REACT_APP_API_HOST_NEW}/${endpoint}`, {
      body: JSON.stringify(body),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${riderToken}`,
        "x-version": process.env.REACT_APP_VERSION_TYPE,
        "X-App-Version": appVersion,
      },
      method: "DELETE",
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.statusCode >= 300) {
          const error = new Error();
          error.status = res.statusCode;
          error.statusCode = res.statusCode;
          error.message = res.error || res.message;

          // Sentry 에러 캡처
          captureSentryError(error, endpoint, "DELETE", {
            body_size: JSON.stringify(body).length,
            has_body: Object.keys(body).length > 0,
          });

          throw error;
        }

        return res;
      })
      .catch((error) => {
        // 네트워크 에러 등도 캡처
        if (!error.status && !error.statusCode) {
          Sentry.captureException(error, {
            tags: {
              error_category: "network_error",
              error_type: "connection_failed",
              api_endpoint: endpoint,
              api_method: "DELETE",
            },
          });
        }
        throw error;
      });
  },
};
